import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as api from '../../../api';
import { supabase } from '../../../lib/supabaseClient';
import Spinner from '../../Atoms/Spinner';
import toast from 'react-hot-toast';

const Container = styled.div`
    max-width: 1600px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const Header = styled.div`
    margin-bottom: 40px;

    h1 {
        font-size: 32px;
        margin-bottom: 8px;
        color: #333;
    }

    .subtitle {
        font-size: 18px;
        color: #666;
    }
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    transition: all 0.2s ease;
    margin-bottom: 20px;

    &:hover {
        background: #e8e8e8;
        transform: translateX(-4px);
    }
`;

const StatsSection = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 40px;
`;

const StatCard = styled.div`
    background: ${props => props.color || 'white'};
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);

    .value {
        font-size: 36px;
        font-weight: 700;
        color: ${props => props.textColor || '#333'};
    }

    .label {
        font-size: 14px;
        color: ${props => props.textColor ? `${props.textColor}aa` : '#666'};
        margin-top: 8px;
    }
`;

const TableSection = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    overflow: hidden;
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #eee;

    h2 {
        font-size: 20px;
        font-weight: 600;
        margin: 0;
    }
`;

const SearchInput = styled.input`
    padding: 10px 16px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    width: 300px;

    &:focus {
        outline: none;
        border-color: #9b59b6;
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;

    th {
        text-align: left;
        padding: 16px 10px;
        background: #f8f9fa;
        font-weight: 600;
        font-size: 13px;
        color: #555;
        border-bottom: 2px solid #eee;
    }

    td {
        padding: 10px 10px;
        border-bottom: 1px solid #f0f0f0;
        font-size: 12px;
        color: #333;
    }

    tr:hover td {
        background: #f8f9fa;
    }
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;

    &.complete {
        background: #d4edda;
        color: #155724;
    }

    &.partial {
        background: #fff3cd;
        color: #856404;
    }

    &.none {
        background: #f8d7da;
        color: #721c24;
    }
`;

const CheckIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 12px;

    &.checked {
        background: #28a745;
        color: white;
    }

    &.unchecked {
        background: #e0e0e0;
        color: #999;
    }
`;

const RefreshButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #9b59b6;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s ease;

    &:hover {
        background: #8e44ad;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ExportButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #28a745;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s ease;
    margin-left: 12px;

    &:hover {
        background: #218838;
    }
`;

const ReminderButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s ease;
    margin-left: 12px;

    &:hover {
        background: #c82333;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const LastUpdate = styled.div`
    font-size: 13px;
    color: #888;
    margin-top: 16px;
    text-align: right;
    padding-right: 24px;
`;

const DownloadLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: #e7f3ff;
    color: #004085;
    border-radius: 6px;
    font-size: 11px;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
        background: #cce5ff;
        text-decoration: none;
    }
`;

const ModifsButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: #f0e6ff;
    color: #5a2d82;
    border: none;
    border-radius: 12px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #e0d0f5;
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    max-width: 500px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;

    h3 {
        margin: 0;
        font-size: 18px;
        color: #333;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #999;
    padding: 0;
    line-height: 1;

    &:hover {
        color: #333;
    }
`;

const EditItem = styled.div`
    margin-bottom: 16px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;

    .field-label {
        font-size: 12px;
        color: #666;
        text-transform: uppercase;
        margin-bottom: 4px;
    }

    .field-value {
        font-size: 14px;
        color: #333;
        font-weight: 500;
    }
`;

const FabricantsList = styled.ul`
    margin: 8px 0 0 0;
    padding-left: 20px;

    li {
        font-size: 13px;
        color: #333;
        margin-bottom: 4px;
    }
`;

// List of winner project IDs
const WINNER_PROJECT_IDS = [
    '67a332e21b103d938bafb0dc', '67a0b8331b103d938bafa954', '67aa1011740d844a3d633e93',
    '6829daa5754f51df2ef9141c', '67e6c18043d5609350e381a8', '67b5e6146890f1a147192905',
    '67be4a58b1ba28c3144aeb73', '67bf2330b1ba28c3144af8d2', '67c090dcdb48cb16a13c5b43',
    '677268097ec5d90c89d6fe63', '67c6b416db48cb16a13c73c7', '67ab652c07736060460b3b75',
    '68348986754f51df2ef9b87c', '67d15108d95ccf24eaf191df', '67d7d38c43d5609350e242a1',
    '67d82d2243d5609350e24c5e', '662adc507159e5b1b1da7c75', '67d8718c43d5609350e25c38',
    '67d1977243d5609350e218f2', '682b5c5f754f51df2ef93bbc', '67da910a43d5609350e274d7',
    '67e1864b43d5609350e2baeb', '67daebf343d5609350e27a9e', '67dd585043d5609350e28e57',
    '67dfeb0e43d5609350e2a360', '67e1452a43d5609350e2b066', '678675e2de3ed4a7001ab532',
    '67e2d05943d5609350e2cf97', '67e3e1cb43d5609350e304e5', '67e3e81543d5609350e308de',
    '67e3f02643d5609350e30a0a', '67e42d7043d5609350e32539', '67e42e5543d5609350e32570',
    '67e45c9843d5609350e329c0', '67e529f743d5609350e33a8c', '67e53aab43d5609350e340c3',
    '67e56ab043d5609350e3490f', '67e5764d43d5609350e34cb5', '67e57f5143d5609350e3519a',
    '67e5864c43d5609350e353a3', '67e58da443d5609350e3561d', '67e58ebc43d5609350e35665',
    '67e5ab6b43d5609350e35905', '67e6828d43d5609350e36b6a', '67e6acaf43d5609350e37832',
    '67e51ddb43d5609350e33667', '67e6c0a843d5609350e380cf', '67e6d53a43d5609350e38725',
    '67e6dfff43d5609350e38900', '67e7003c43d5609350e38bfc', '6834625c754f51df2ef9a4b1',
    '67ea99b76b7945ed1a576bae', '67f90250a0262e085d37088f', '67e1741a43d5609350e2b608',
    '67fe7a6c9167c049fff4e37e', '67ab6ade07736060460b3c16', '6810a343264e7745556acae1',
    '6800f4c59167c049fff51f5d', '6800b8529167c049fff50e47', '6800e76e9167c049fff5197f',
    '6800ef849167c049fff51c6f', '6800fad59167c049fff52224', '680113739167c049fff52d73',
    '680106139167c049fff525af', '680125219167c049fff53405', '680957ab9167c049fff5a7c2',
    '681164d36c8fbfe5e3c91e9e', '68020ef79167c049fff54015', '68025d069167c049fff56132',
    '680b728c9167c049fff5c8f7', '680249249167c049fff55a19', '68024ed89167c049fff55b90',
    '68025b1a9167c049fff56037', '680270379167c049fff566f5', '680274c09167c049fff567c8',
    '680275419167c049fff567de', '68027b389167c049fff56954', '680291609167c049fff56cad',
    '6802a3fc9167c049fff56f5f', '6849787b17dde4ca4c4bf043', '6849435a17dde4ca4c4be251',
    '6807cb759167c049fff58dd4', '6808c3f59167c049fff599c7', '6808c3859167c049fff599b1',
    '680fa4b19167c049fff5deee', '6801e2f49167c049fff53ce1', '680a54a09167c049fff5bbc4',
    '680b94b29167c049fff5cd49', '6810df4a264e7745556ad75d', '680fa3f89167c049fff5deb1',
    '680fa844a71ddf126b77b977', '680fa9f09167c049fff5e027', '6810c9b9264e7745556acf4d',
    '681125b66c8fbfe5e3c91c62', '6818cac19e656d77bc6c72fa', '6818d27e9e656d77bc6c75a3',
    '6818dbdf9e656d77bc6c78a0', '682d97b2754f51df2ef96070', '67fccffaa0262e085d370ddc',
    '680271809167c049fff5674a'
];

const FD100SuiviLaureats = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const eventId = searchParams.get('id') || '67590da5499bcc995c210b40'; // Default FD100 2025

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [laureatsData, setLaureatsData] = useState([]);
    const [trackingData, setTrackingData] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [lastUpdate, setLastUpdate] = useState(null);
    const [editsModal, setEditsModal] = useState(null); // { laureat, tracking }
    const [sendingReminders, setSendingReminders] = useState(false);

    useEffect(() => {
        fetchData();
    }, [eventId]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Fetch all participating users with their data
            const response = await api.getParticipatingUsersWithData({ eventId });

            // Filter for winners only
            const winners = [];
            response.data.forEach(user => {
                if (user.participation.data.projets) {
                    user.participation.data.projets.forEach(project_id => {
                        const project = user.company.projects.find((projet) => projet._id === project_id);
                        if (project && project.status === "validé" && WINNER_PROJECT_IDS.includes(project._id)) {
                            winners.push({
                                user,
                                project,
                                userId: user.user._id,
                                projectId: project._id,
                                email: user.hsProfile?.email || user.user?.email,
                                userName: `${user.hsProfile?.firstname || ''} ${user.hsProfile?.lastname || ''}`.trim(),
                                companyName: user.hsCompany?.properties?.name || '',
                                projectName: project.name_en || project.name
                            });
                        }
                    });
                }
            });

            setLaureatsData(winners);

            // Fetch tracking data from Supabase
            const { data: trackingRecords, error } = await supabase
                .from('fd100_laureats_tracking')
                .select('*')
                .eq('event_id', eventId);

            if (error) {
                console.error('Error fetching tracking data:', error);
            } else {
                // Create a map of tracking data by project_id
                const trackingMap = {};
                trackingRecords?.forEach(record => {
                    trackingMap[record.project_id] = record;
                });
                setTrackingData(trackingMap);
            }

            setLastUpdate(new Date());
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchData();
        setRefreshing(false);
        toast.success('Données actualisées');
    };

    const getValidationStatus = (tracking, project) => {
        // Check if project type is "objet" - objects don't have a country field
        // Objet: Nom, Créateur, École, Fabricants, Règlement = 5 fields
        // Espace: Nom, Créateur, Pays, École, Fabricants, Règlement = 6 fields
        const isObjet = project?.type === "objet";
        const totalValidations = isObjet ? 5 : 6;

        if (!tracking) return { status: 'none', count: 0, total: totalValidations };

        // Count fabricants for this project
        let fabricantsCount = 0;
        try {
            const fabricants = project?.fabricants ? JSON.parse(project.fabricants) : [];
            fabricantsCount = Array.isArray(fabricants) ? fabricants.length : 0;
        } catch { }

        // Check if all fabricants are validated (validated_fabricants is now a jsonb array)
        const validatedFabricants = tracking.validated_fabricants || [];
        const allFabricantsValidated = fabricantsCount > 0
            ? (Array.isArray(validatedFabricants) && validatedFabricants.length === fabricantsCount)
            : true; // If no fabricants, consider it validated

        // Build validations array - exclude country for objet projects
        const validations = [
            tracking.validated_project_name,
            tracking.validated_creator_name,
            tracking.validated_school,
            allFabricantsValidated,
            tracking.reglement_uploaded
        ];

        // Add country validation only for espace projects
        if (!isObjet) {
            validations.splice(2, 0, tracking.validated_country); // Insert at index 2 (after creator)
        }

        const count = validations.filter(Boolean).length;

        if (count === totalValidations) return { status: 'complete', count, total: totalValidations };
        if (count > 0) return { status: 'partial', count, total: totalValidations };
        return { status: 'none', count, total: totalValidations };
    };

    const getFabricantsValidationStatus = (tracking, project) => {
        let fabricantsCount = 0;
        try {
            const fabricants = project?.fabricants ? JSON.parse(project.fabricants) : [];
            fabricantsCount = Array.isArray(fabricants) ? fabricants.length : 0;
        } catch { }

        if (fabricantsCount === 0) return { validated: true, count: 0, total: 0 };

        const validatedFabricants = tracking?.validated_fabricants || [];
        const validatedCount = Array.isArray(validatedFabricants) ? validatedFabricants.length : 0;

        return {
            validated: validatedCount === fabricantsCount,
            count: validatedCount,
            total: fabricantsCount
        };
    };

    const hasEdits = (tracking) => {
        return !!(
            tracking?.edited_project_name ||
            tracking?.edited_creator_name ||
            tracking?.edited_country ||
            tracking?.edited_school ||
            tracking?.edited_fabricants
        );
    };

    const getEditedFabricants = (tracking) => {
        if (!tracking?.edited_fabricants) return null;
        try {
            const parsed = JSON.parse(tracking.edited_fabricants);
            return Array.isArray(parsed) ? parsed : null;
        } catch {
            return null;
        }
    };

    const openEditsModal = (laureat, tracking) => {
        setEditsModal({ laureat, tracking });
    };

    const closeEditsModal = () => {
        setEditsModal(null);
    };

    const getStats = () => {
        const total = laureatsData.length;
        let validationsComplete = 0;
        let validationsPartial = 0;
        let documentsUploaded = 0;
        let withEdits = 0;

        laureatsData.forEach(laureat => {
            const tracking = trackingData[laureat.projectId];
            const validation = getValidationStatus(tracking, laureat.project);

            if (validation.status === 'complete') validationsComplete++;
            else if (validation.status === 'partial') validationsPartial++;

            if (tracking?.reglement_uploaded) documentsUploaded++;
            if (hasEdits(tracking)) withEdits++;
        });

        return {
            total,
            validationsComplete,
            validationsPartial,
            validationsNone: total - validationsComplete - validationsPartial,
            documentsUploaded,
            withEdits
        };
    };

    const filteredData = laureatsData
        .filter(laureat => {
            const search = searchTerm.toLowerCase();
            return (
                laureat.userName?.toLowerCase().includes(search) ||
                laureat.companyName?.toLowerCase().includes(search) ||
                laureat.projectName?.toLowerCase().includes(search) ||
                laureat.email?.toLowerCase().includes(search)
            );
        })
        .sort((a, b) => {
            // Sort by validation status: none -> partial -> complete
            const aValidation = getValidationStatus(trackingData[a.projectId], a.project);
            const bValidation = getValidationStatus(trackingData[b.projectId], b.project);

            const statusOrder = { 'none': 0, 'partial': 1, 'complete': 2 };
            return statusOrder[aValidation.status] - statusOrder[bValidation.status];
        });

    const exportToCSV = () => {
        const headers = [
            'Nom',
            'Entreprise',
            'Email',
            'Projet',
            'Nom validé',
            'Créateur validé',
            'Pays validé',
            'École validée',
            'Fabricants validés',
            'Règlement uploadé',
            'A des modifications',
            'Nom modifié',
            'Créateur modifié',
            'Pays modifié',
            'École modifiée',
            'Dernière mise à jour'
        ];

        const rows = laureatsData.map(laureat => {
            const tracking = trackingData[laureat.projectId];
            const fabStatus = getFabricantsValidationStatus(tracking, laureat.project);
            const isObjet = laureat.project?.type === "objet";
            return [
                laureat.userName,
                laureat.companyName,
                laureat.email,
                laureat.projectName,
                tracking?.validated_project_name ? 'Oui' : 'Non',
                tracking?.validated_creator_name ? 'Oui' : 'Non',
                isObjet ? 'N/A' : (tracking?.validated_country ? 'Oui' : 'Non'),
                tracking?.validated_school ? 'Oui' : 'Non',
                fabStatus.total > 0 ? `${fabStatus.count}/${fabStatus.total}` : 'N/A',
                tracking?.reglement_uploaded ? 'Oui' : 'Non',
                hasEdits(tracking) ? 'Oui' : 'Non',
                tracking?.edited_project_name || '-',
                tracking?.edited_creator_name || '-',
                isObjet ? 'N/A' : (tracking?.edited_country || '-'),
                tracking?.edited_school || '-',
                tracking?.updated_at ? new Date(tracking.updated_at).toLocaleString('fr-FR') : '-'
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `suivi_laureats_fd100_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();

        toast.success('Export CSV téléchargé');
    };

    const handleSendReminders = async () => {
        if (!window.confirm('Envoyer un email de rappel à tous les lauréats qui n\'ont pas encore commencé ?')) {
            return;
        }

        setSendingReminders(true);

        try {
            // Get all laureates who haven't started yet
            const incompleteList = laureatsData
                .filter(laureat => {
                    const tracking = trackingData[laureat.projectId];
                    const validation = getValidationStatus(tracking, laureat.project);
                    return validation.status !== 'complete';
                })
                .map(laureat => {
                    const tracking = trackingData[laureat.projectId];
                    const isObjet = laureat.project?.type === "objet";
                    const fabStatus = getFabricantsValidationStatus(tracking, laureat.project);

                    // Build list of missing steps
                    const missingSteps = [];
                    if (!tracking?.validated_project_name) missingSteps.push('Nom du projet');
                    if (!tracking?.validated_creator_name) missingSteps.push('Nom du créateur');
                    if (!isObjet && !tracking?.validated_country) missingSteps.push('Pays');
                    if (!tracking?.validated_school) missingSteps.push('Formation / École');
                    if (!fabStatus.validated) missingSteps.push(`Fabricants (${fabStatus.count}/${fabStatus.total} validés)`);
                    if (!tracking?.validated_key_visual) missingSteps.push('Key Visual');
                    if (!tracking?.reglement_uploaded) missingSteps.push('Règlement signé');

                    return {
                        projectId: laureat.projectId,
                        email: laureat.email,
                        userName: laureat.userName,
                        projectName: laureat.projectName,
                        missingSteps
                    };
                });

            if (incompleteList.length === 0) {
                toast.info('Aucun lauréat qui n\'a pas encore commencé');
                setSendingReminders(false);
                return;
            }

            const response = await api.sendFD100ReminderEmails({
                eventId,
                recipients: incompleteList
            });

            if (response.status === 'ok') {
                toast.success(`${incompleteList.length} email(s) de rappel envoyé(s)`);
            } else {
                toast.error('Erreur lors de l\'envoi des emails');
            }
        } catch (error) {
            console.error('Error sending reminders:', error);
            toast.error('Erreur lors de l\'envoi des emails');
        } finally {
            setSendingReminders(false);
        }
    };

    if (loading) return <Spinner />;

    const stats = getStats();

    return (
        <Container>
            <BackButton onClick={() => navigate('/admin/events')}>
                ← Retour aux événements
            </BackButton>

            <Header>
                <h1>Suivi Lauréats FD100 2025</h1>
                <div className="subtitle">Suivez les validations et documents des lauréats</div>
            </Header>

            <StatsSection>
                <StatCard>
                    <div className="value">{stats.total}</div>
                    <div className="label">Lauréats total</div>
                </StatCard>
                <StatCard color="#d4edda" textColor="#155724">
                    <div className="value">{stats.validationsComplete}</div>
                    <div className="label">Validations complètes</div>
                </StatCard>
                <StatCard color="#fff3cd" textColor="#856404">
                    <div className="value">{stats.validationsPartial}</div>
                    <div className="label">Validations partielles</div>
                </StatCard>
                <StatCard color="#f8d7da" textColor="#721c24">
                    <div className="value">{stats.validationsNone}</div>
                    <div className="label">Pas encore commencé</div>
                </StatCard>
                <StatCard color="#e7f3ff" textColor="#004085">
                    <div className="value">{stats.documentsUploaded}</div>
                    <div className="label">Règlements uploadés</div>
                </StatCard>
                <StatCard color="#f0e6ff" textColor="#5a2d82">
                    <div className="value">{stats.withEdits}</div>
                    <div className="label">Avec modifications</div>
                </StatCard>
            </StatsSection>

            <TableSection>
                <TableHeader>
                    <h2>Liste des lauréats ({filteredData.length})</h2>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <SearchInput
                            type="text"
                            placeholder="Rechercher par nom, entreprise, projet..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <RefreshButton onClick={handleRefresh} disabled={refreshing} style={{ marginLeft: 12 }}>
                            {refreshing ? 'Actualisation...' : '↻ Actualiser'}
                        </RefreshButton>
                        <ExportButton onClick={exportToCSV}>
                            ↓ Export CSV
                        </ExportButton>
                        <ReminderButton onClick={handleSendReminders} disabled={sendingReminders}>
                            {sendingReminders ? 'Envoi en cours...' : '✉ Relancer non commencés'}
                        </ReminderButton>
                    </div>
                </TableHeader>

                <div style={{ overflowX: 'auto' }}>
                    <Table>
                        <thead>
                            <tr>
                                <th>Lauréat</th>
                                <th>Entreprise</th>
                                <th>Projet</th>
                                <th>Statut validation</th>
                                <th>Nom</th>
                                <th>Créateur</th>
                                <th>Pays</th>
                                <th>École</th>
                                <th>Fabricants</th>
                                <th>Règlement</th>
                                <th>Modifs</th>
                                <th>Dernière MAJ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((laureat, index) => {
                                const tracking = trackingData[laureat.projectId];
                                const validation = getValidationStatus(tracking, laureat.project);
                                const fabStatus = getFabricantsValidationStatus(tracking, laureat.project);

                                return (
                                    <tr
                                        key={`${laureat.projectId}-${index}`}
                                        style={{
                                            backgroundColor:
                                                validation.status === 'complete' ? '#f0f9f0' :
                                                validation.status === 'partial' ? '#fffbf0' :
                                                'transparent'
                                        }}
                                    >
                                        <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            <div style={{ fontWeight: 500 }}>{laureat.userName || '-'}</div>
                                            <div style={{ fontSize: 12, color: '#888'}}>{laureat.email}</div>
                                        </td>
                                        <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12}}>{laureat.companyName || '-'}</td>
                                        <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12}}>
                                            {laureat.projectName || '-'}
                                        </td>
                                        <td>
                                            <StatusBadge className={validation.status}>
                                                {validation.status === 'complete' && '✓ Complet'}
                                                {validation.status === 'partial' && `${validation.count}/${validation.total}`}
                                                {validation.status === 'none' && '○ Non commencé'}
                                            </StatusBadge>
                                        </td>
                                        <td>
                                            <CheckIcon className={tracking?.validated_project_name ? 'checked' : 'unchecked'}>
                                                {tracking?.validated_project_name ? '✓' : '○'}
                                            </CheckIcon>
                                        </td>
                                        <td>
                                            <CheckIcon className={tracking?.validated_creator_name ? 'checked' : 'unchecked'}>
                                                {tracking?.validated_creator_name ? '✓' : '○'}
                                            </CheckIcon>
                                        </td>
                                        <td>
                                            {laureat.project?.type === "objet" ? (
                                                <span style={{ fontSize: 12, color: '#999' }}>N/A</span>
                                            ) : (
                                                <CheckIcon className={tracking?.validated_country ? 'checked' : 'unchecked'}>
                                                    {tracking?.validated_country ? '✓' : '○'}
                                                </CheckIcon>
                                            )}
                                        </td>
                                        <td>
                                            <CheckIcon className={tracking?.validated_school ? 'checked' : 'unchecked'}>
                                                {tracking?.validated_school ? '✓' : '○'}
                                            </CheckIcon>
                                        </td>
                                        <td>
                                            {fabStatus.total > 0 ? (
                                                <span style={{
                                                    fontSize: 12,
                                                    padding: '4px 8px',
                                                    borderRadius: 12,
                                                    background: fabStatus.validated ? '#d4edda' : '#fff3cd',
                                                    color: fabStatus.validated ? '#155724' : '#856404'
                                                }}>
                                                    {fabStatus.count}/{fabStatus.total}
                                                </span>
                                            ) : (
                                                <span style={{ fontSize: 12, color: '#999' }}>N/A</span>
                                            )}
                                        </td>
                                        <td>
                                            {tracking?.reglement_uploaded ? (
                                                tracking?.reglement_file_path ? (
                                                    <DownloadLink
                                                        href={tracking.reglement_file_path}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Télécharger le règlement signé"
                                                    >
                                                        ↓ PDF
                                                    </DownloadLink>
                                                ) : (
                                                    <CheckIcon className="checked">✓</CheckIcon>
                                                )
                                            ) : (
                                                <CheckIcon className="unchecked">○</CheckIcon>
                                            )}
                                        </td>
                                        <td>
                                            {hasEdits(tracking) ? (
                                                <ModifsButton onClick={() => openEditsModal(laureat, tracking)}>
                                                    ✎ Voir
                                                </ModifsButton>
                                            ) : (
                                                <span style={{ fontSize: 12, color: '#999' }}>-</span>
                                            )}
                                        </td>
                                        <td style={{ fontSize: 12, color: '#888' }}>
                                            {tracking?.updated_at
                                                ? new Date(tracking.updated_at).toLocaleString('fr-FR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })
                                                : '-'
                                            }
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>

                <LastUpdate>
                    Dernière actualisation : {lastUpdate?.toLocaleString('fr-FR')}
                </LastUpdate>
            </TableSection>

            {/* Edits Modal */}
            {editsModal && (
                <ModalOverlay onClick={closeEditsModal}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <h3>Modifications de {editsModal.laureat.userName || 'l\'utilisateur'}</h3>
                            <CloseButton onClick={closeEditsModal}>&times;</CloseButton>
                        </ModalHeader>

                        <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
                            Projet : <strong>{editsModal.laureat.projectName}</strong>
                        </div>

                        {editsModal.tracking?.edited_project_name && (
                            <EditItem>
                                <div className="field-label">Nom du projet</div>
                                <div className="field-value">{editsModal.tracking.edited_project_name}</div>
                            </EditItem>
                        )}

                        {editsModal.tracking?.edited_creator_name && (
                            <EditItem>
                                <div className="field-label">Nom du créateur</div>
                                <div className="field-value">{editsModal.tracking.edited_creator_name}</div>
                            </EditItem>
                        )}

                        {editsModal.tracking?.edited_country && (
                            <EditItem>
                                <div className="field-label">Pays</div>
                                <div className="field-value">{editsModal.tracking.edited_country}</div>
                            </EditItem>
                        )}

                        {editsModal.tracking?.edited_school && (
                            <EditItem>
                                <div className="field-label">Formation / École</div>
                                <div className="field-value">{editsModal.tracking.edited_school}</div>
                            </EditItem>
                        )}

                        {getEditedFabricants(editsModal.tracking) && (
                            <EditItem>
                                <div className="field-label">Fabricants</div>
                                <FabricantsList>
                                    {getEditedFabricants(editsModal.tracking).map((fab, idx) => (
                                        <li key={idx}>{fab.name || fab}</li>
                                    ))}
                                </FabricantsList>
                            </EditItem>
                        )}

                        {editsModal.tracking?.last_edit_update && (
                            <div style={{ fontSize: 12, color: '#888', marginTop: 16, textAlign: 'right' }}>
                                Dernière modification : {new Date(editsModal.tracking.last_edit_update).toLocaleString('fr-FR')}
                            </div>
                        )}
                    </ModalContent>
                </ModalOverlay>
            )}
        </Container>
    );
};

export default FD100SuiviLaureats;
